import { useState, useCallback, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { zapfHumanistRegular } from "@/app/jewellery/font";
import CountryCodePopup from "@/components/CustomUI/modals/CountryCodePopup";
import { Button } from "@/components/ui/button";
import { toast } from "react-toastify";
import { validateEmail } from "@/lib/utils";
import { validatePhoneApi, chatApi } from "@/clientSideApi/core";
import { setExpertInfo, setUtmInfo } from "@/store/general/generalSlice";
import { countryCodes } from "@/constants/country-codes";
import { getUserDetails } from "@/lib/user";
import {
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const THANKYOU_MSG = "Thank you for sharing your number. Our expert from the team will connect with you over whatsapp and will share the details shortly.";
const DEFAULT_COUNTRY_CODE = "+91";

const Container = ({ onOpenChange }) => {
  
  const [loading, setLoading] = useState(false);
  const [popupVisible, setPopupVisible] = useState(false);
  const dispatch = useDispatch();

  const { user, utm } = useSelector((state) => state.general);

  const [formData, setFormData] = useState({
    phone_0: DEFAULT_COUNTRY_CODE,
    phone_1: "",
    email: ""
  })

  const handlePopupVisible = useCallback((val) => {
    setPopupVisible(val)
  }, []);

  const handleChange = useCallback((val) => {
    setFormData(prev => ({ ...prev, "phone_0": val }));
    handlePopupVisible(false);
  }, [handlePopupVisible])

  const handleChangePhone = useCallback((e) => {
    const val = e.target.value;
    setFormData(prev => ({ ...prev, "phone_1": val }));
  }, []);

  const handleChangeEmail = useCallback((e) => {
    const val = e.target.value;
    setFormData(prev => ({ ...prev, "email": val }));
  }, []);

  const handleKeyDown = (e) => {
    if (e.keyCode === 13) onSubmit(e);
  };

  const validateFormData = async () => {
    if (!formData.phone_1) {
      toast.info(
        "Phone number Required.",
        {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
        }
      );
      return false;
    }
    if (!validateEmail(formData.email)) {
      toast.info(
        "Invalid Email ID",
        {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
        }
      );
      return false;
    }
    const apiPayload = { phone: formData.phone_0 + formData.phone_1 };
    const isPhoneValid = await validatePhoneApi(apiPayload);
    return isPhoneValid;
  };

  const submitForm = async (data) => {
    const url = "/api/rest/v1/chat/" + (utm ? "?" + utm : "");
    const chatApiResponse = await chatApi(url, data);
    if (!chatApiResponse) return;
    const raw_data = JSON.stringify(chatApiResponse);
    localStorage.setItem("expert_info", raw_data);
    dispatch(setExpertInfo(chatApiResponse));
    dispatch(setUtmInfo(null));
    onOpenChange(false);
    toast.success(THANKYOU_MSG, {
      position: "top-center",
      autoClose: 3500,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: "colored",
    });
    if (window.hasOwnProperty("fbq")) {
      fbq('trackCustom', 'popup_submit', {phone: data.phone_0 + data.phone_1,});
    }
    gtag("event", "popup_submit", {
      phone: data.phone_0 + data.phone_1,
    });
  };

  const onSubmit = async (e) => {
    e?.preventDefault();
    if (loading) return;
    setLoading(true);
    const formIsValid = await validateFormData();
    if (!formIsValid) {
      setLoading(false);
      return;
    }
    const { phone_0, phone_1, email } = formData;
    const apiPayload = { phone_0, phone_1, email, lead_source: "webpopup" };
    await submitForm(apiPayload);
    setLoading(false);
  };

  useEffect(() => {
    if(user){
      const userDetails = getUserDetails(user);
      const email = userDetails.email;
      let phone_0 = DEFAULT_COUNTRY_CODE;
      let phone_1;
      if(userDetails.phone){
        const countryCodeArr = countryCodes.find(ele => userDetails.phone.includes(ele.value));

        if(countryCodeArr.length > 0){
          phone_0 = countryCodeArr[0];
          phone_1 = userDetails.phone.replace(countryCodeArr[0], "");
        }
      }
      setFormData({ email, phone_0, phone_1 });
    }
  }, [ user ])

  return (
    <DialogContent
      className={`
            ${zapfHumanistRegular.className}
            tracking-wide	pop-up gap-x-4 gap-y-2
            min-w-[35vw] min-h-[35vh]
            flex flex-col justify-center
        `}
    >
      <DialogHeader
        className="sm:text-center text-base md:text-lg lg:text-xl desktop:text-2xl font-black m-0"
      >
        Welcome Aboard!
      </DialogHeader>
      <DialogTitle
        className="
            text-base xl:text-lg desktop:text-xl font-black
            text-center 
        "
      >
        Here is a special discount for you. Enter your number to access.
      </DialogTitle>
      <form
        className="
          py-7 px-3 flex flex-col text-center 
          items-center justify-center
        "
        onSubmit={onSubmit}
      >
        <div
          className="
            flex w-full h-[42px] mb-3 border-[1px] 
            border-[#bdbdbd] rounded-sm"
        >
          <CountryCodePopup
            selected={formData.phone_0}
            onChange={handleChange}
            popupVisible={popupVisible}
            handlePopupVisible={handlePopupVisible}
            titleHidden={true}
            columns={1}
          />
          <input
            className="
              p-2 outline-0 text-font-primary
              text-sm md:text-base 
              w-full h-full
              placeholder:text-sm
              placeholder:md:text-base
              placeholder:text-gray-700	 
              tracking-wide
            "
            placeholder="Whatsapp Number"
            type="phone"
            required
            value={formData.phone_1}
            onChange={handleChangePhone}
            onKeyDown={handleKeyDown}
          />
        </div>
        <input
          className="
            p-2 border-[1px] border-[#bdbdbd] outline-0 
            text-font-primary text-sm md:text-base  
            w-full placeholder:text-[10px] 
            placeholder:text-sm placeholder:md:text-base 
            mb-3 rounded-sm placeholder:text-gray-700	
            tracking-wide
          "
          placeholder="Email (Optional)"
          type="email"
          value={formData.email}
          onChange={handleChangeEmail}
        />
        <Button
          aria-label="subscribe newsletter"
          variant="primary"
          className="
            flex justify-center items-center w-[45px] 
            bg-[#45d4d5] border-[1px] border-[#3e9f8e] 
            text-white p-[7px] select-none touch-manipulation 
            transition duration-150 ease-in-out hover:drop-shadow-md
            rounded-sm h-[42px] w-full tracking-widest
            text-base xl:text-lg desktop:text-xl
          "
          type="submit"
        >
          DISCOVER EXCLUSIVE OFFERS
        </Button>
      </form>
      <Button
        aria-label="subscribe newsletter"
        variant="primary"
        className="
            bg-transparent border-0 text-black
            p-0 select-none touch-manipulation 
            transition duration-150 ease-in-out 
            hover:drop-shadow-md rounded-sm h-auto 
            w-full font-medium	
            text-sm xl:text-base desktop:text-xl
          "
        type="button"
        onClick={() => onOpenChange(false)}
      >
        No I love to pay full price!
      </Button>
    </DialogContent>
  );
};

export default Container;
